import { createMuiTheme, unstable_createMuiStrictModeTheme } from '@material-ui/core/styles';
import { Theme, ThemeOptions } from '@material-ui/core/styles/createMuiTheme';

export type MuiColor = {
  primary?: string;
  _500: string;
  _400: string;
  _300: string;
  _200: string;
  _100: string;
  _75?: string;
  _50: string;
};
export const colorPalette = {
  primaryColorGreen: {
    primary: '#62CA76',
    _500: '#4BB760',
    _400: '#62CA76',
    _300: '#7AD28B',
    _200: '#89D798',
    _100: '#A9E2B4',
    _75: '#C8ECCF',
    _50: '#EFFAF1',
  },
  primaryColorBlue: {
    primary: '#0053CB',
    _500: '#0053CB',
    _400: '#1A64D0',
    _300: '#4D87DB',
    _200: '#8CB2E8',
    _100: '#B3CBEF',
    _75: '#D9E5F7',
    _50: '#F3F7FC',
  },
  red: {
    _500: '#D44F49',
    _400: '#E45851',
    _300: '#E76963',
    _200: '#EC8A86',
    _100: '#F1ACA8',
    _75: '#F8D5D4',
    _50: '#FCF0F0',
  },
  orange: {
    _500: '#FCB017',
    _400: '#FFBC21',
    _300: '#FEC63D',
    _200: '#FDD570',
    _100: '#FDE7B1',
    _75: '#FEF3CA',
    _50: '#FEFAE7',
  },
  dark: {
    _900: '#141518',
    _800: '#3B3F45',
    _700: '#6D7277',
    _600: '#8D9195',
    _500: '#A8AAAC',
  },
  gray: {
    _400: '#C2C2C2',
    _300: '#DEDEDE',
    _200: '#EAEAEA',
    _100: '#F6F6F6',
    _50: '#FBFBFB',
  },
  purpleGray: {
    _500: '#8181A5',
    _400: '#9F9FBD',
    _300: '#C0C0D2',
    _200: '#DFDFE8',
    _100: '#ECECF2',
    _50: '#F5F5FA',
  },
  general: {
    text2: '#464663',
    backgroundGray: '#F2F5FE',
    feedBackground: '#F6F6F6',
  },
};

export enum TextSizesEnum {
  XXSmall = 1,
  XSmall = 1,
  Small,
  Normal,
  NormalPlus,
  Large,
  XLarge,
  XXLarge,
  XXXLarge,
}

interface TextSizesInterface {
  fontSize: number;
  lineHeight: string;
}

export const textSizes: { [key in TextSizesEnum]: TextSizesInterface } = {
  [TextSizesEnum.XXSmall]: {
    fontSize: 8,
    lineHeight: '16px',
  },
  [TextSizesEnum.XSmall]: {
    fontSize: 10,
    lineHeight: '16px',
  },
  [TextSizesEnum.Small]: {
    fontSize: 12,
    lineHeight: '16px',
  },
  [TextSizesEnum.Normal]: {
    fontSize: 14,
    lineHeight: '20px',
  },
  [TextSizesEnum.NormalPlus]: {
    fontSize: 16,
    lineHeight: '24px',
  },
  [TextSizesEnum.Large]: {
    fontSize: 18,
    lineHeight: '24px',
  },
  [TextSizesEnum.XLarge]: {
    fontSize: 22,
    lineHeight: '28px',
  },
  [TextSizesEnum.XXLarge]: {
    fontSize: 28,
    lineHeight: '34px',
  },
  [TextSizesEnum.XXXLarge]: {
    fontSize: 34,
    lineHeight: '40px',
  },
};

export const spacings = {
  xsmall: 4,
  small: 8,
  normal: 16,
  xlarge: 24,
  xxlarge: 32,
};

export const bottomBorderStyle = {
  borderBottomWidth: 1,
  borderBottomColor: colorPalette.purpleGray._100,
  borderBottomStyle: 'solid',
} as const;

export const topBorderStyle = {
  borderTopWidth: 1,
  borderTopColor: colorPalette.purpleGray._100,
  borderTopStyle: 'solid',
} as const;

export const rightBorderStyle = {
  borderRightWidth: 1,
  borderRightColor: colorPalette.purpleGray._100,
  borderRightStyle: 'solid',
} as const;

export const leftBorderStyle = {
  borderLeftWidth: 1,
  borderLeftColor: colorPalette.purpleGray._100,
  borderLeftStyle: 'solid',
} as const;

export const muiThemeContent: ThemeOptions = {
  // shadows: Array(25).fill('none') as Shadows,
  palette: {
    primary: { main: colorPalette.primaryColorGreen.primary },
  },
  typography: {
    // if we need more styles (such as font-weight: 300, embed more styles from here: https://fonts.google.com/specimen/Inter
    // and add the stylesheet to the public/index.html
    fontFamily: 'Inter',
    button: {
      textTransform: 'none',
      fontWeight: 'bold',
    },
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        html: {
          display: 'flex',
        },
      },
    },
    MuiAppBar: {
      root: {
        height: 80,
      },
    },
    MuiToolbar: {
      root: {
        height: 80,
      },
    },
    MuiButton: {
      root: {
        borderRadius: 18,
        paddingTop: spacings.small,
        paddingBottom: spacings.small,
        paddingLeft: spacings.normal,
        paddingRight: spacings.normal,
        whiteSpace: 'nowrap',
        flexDirection: 'row',
      },
      label: {
        // TODO: change after fixing all button backgrounds
        // color: '#fff',
      },
      contained: {
        '&:hover': {
          boxShadow: 'none',
        },
        '&:active': {
          boxShadow: 'none',
        },
        // not hovered:
        boxShadow: 'none',
      },
      containedSizeSmall: {
        padding: '8px 16px',
        fontSize: textSizes[TextSizesEnum.Small].fontSize,
        lineHeight: textSizes[TextSizesEnum.Small].lineHeight,
      },
      outlined: {
        color: '#f00 !important',
      },
    },
  },
};

export function createTheme(): Theme {
  if (process.env.NODE_ENV === 'development') {
    return unstable_createMuiStrictModeTheme(muiThemeContent);
  } else {
    return createMuiTheme(muiThemeContent);
  }
}

export const muiTheme = createTheme();
